import { useEffect, useMemo } from 'react';
import useRequestState from '../hooks/useRequestState';
import { LocalAuthorityAndWards } from '../types/filters.type';
import type { OptionType } from '../types/forms.type';
import {
  getLocalAuthorityOptions, getSnapshotOptions, getWardOptions,
} from '../utils/filters';
import GetLocalAuthorityWardSnapshot from '../services/adapters/localAuthorityWardSnapshot';
import { All } from '../types/enums';
import Dropdown from './form-inputs/inputDropdown';

interface SnapshotWardDropdownProps {
  selectedSnapshot: OptionType;
  selectedWard: OptionType;
  selectedLocalAuthority: OptionType;
  onSnapshotChange: (option: OptionType) => void;
  onWardChange: (option: OptionType) => void;
  onLocalAuthorityChange: (option: OptionType) => void;
}

export default function SnapshotWardDropdown({
  selectedSnapshot,
  selectedWard,
  selectedLocalAuthority,
  onSnapshotChange,
  onWardChange,
  onLocalAuthorityChange,

}: SnapshotWardDropdownProps) {
  const {
    data: dropdownData,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<Record<string, Record<string, LocalAuthorityAndWards>>>();

  useEffect(() => {
    const adapter = new GetLocalAuthorityWardSnapshot();
    const response = adapter.adaptResponse();
    void handleRequest(response);
  }, []);
  const snapshotOptions: OptionType[] = dropdownData ? getSnapshotOptions(dropdownData) : [];
  const localAuthorityOptions: OptionType[] = useMemo(
    () => (dropdownData ? getLocalAuthorityOptions(selectedSnapshot.value, dropdownData) : []),
    [dropdownData, selectedSnapshot.value],
  );
  const effectiveLocalAuthority = localAuthorityOptions.length === 1 ? localAuthorityOptions[0].value : selectedLocalAuthority.value;

  useEffect(() => {
    if (localAuthorityOptions.length === 1 && selectedLocalAuthority.value !== localAuthorityOptions[0].value) {
      onLocalAuthorityChange(localAuthorityOptions[0]);
    }
  }, [localAuthorityOptions, onLocalAuthorityChange, selectedLocalAuthority.value]);

  const wardOptions: OptionType[] = dropdownData ? getWardOptions(selectedSnapshot.value, effectiveLocalAuthority, dropdownData) : [];

  let wardDropdownLoadingMessage = '';
  if (!selectedSnapshot.value) {
    wardDropdownLoadingMessage = localAuthorityOptions.length > 1 ? 'Please select snapshot & local authority first' : 'Please select snapshot first';
  } else if (localAuthorityOptions.length > 1 && String(selectedLocalAuthority.value) === String(All.value)) {
    wardDropdownLoadingMessage = 'Please select local authority first';
  } else {
    wardDropdownLoadingMessage = 'Loading Wards...';
  }

  return (
    <div id="snapshot-ward-dropdown-container" className="flex items-center justify-between w-full">
      <FallbackWrapper loadingMessage="Loading filters...">
        {dropdownData && (
        <div className="flex gap-4">
          <Dropdown
            id="snapshot-period-dropdown"
            placeholder="Select Snapshot"
            label=""
            selected={selectedSnapshot.value}
            options={snapshotOptions}
            onChange={(id, value) => (value ? onSnapshotChange(value) : onSnapshotChange({ label: '', value: '' }))}
            placement="bottom"
          />
          {localAuthorityOptions.length > 1 && (
          <Dropdown
            id="localAuthority-period-dropdown"
            placeholder="Select Local Authority"
            label=""
            loadingMessage={selectedSnapshot.value ? 'Loading Local Authorities...' : 'Please select snapshot first'}
            selected={selectedLocalAuthority.value}
            options={localAuthorityOptions}
            onChange={(id, value) => (value ? onLocalAuthorityChange(value) : onLocalAuthorityChange({ label: '', value: '' }))}
            placement="bottom"
          />
          )}

          <Dropdown
            id="ward-period-dropdown"
            placeholder={`${selectedLocalAuthority.value ? 'Select Ward' : 'Please select local authority first'}`}
            label=""
            loadingMessage={wardDropdownLoadingMessage}
            selected={selectedWard.value}
            options={wardOptions}
            onChange={(id, value) => (value ? onWardChange(value) : onWardChange({ label: '', value: '' }))}
            placement="bottom"
          />
        </div>
        )}
      </FallbackWrapper>
    </div>
  );
}
